import { Loadable } from '@/components/loaders';
import { AudienceDetailSlideOver } from '@/features/audiences/components/slideOver/audienceDetailSlideOver/AudienceDetailSlideOver';
import { setCreateCampaignAudiencesPagination } from '@/features/campaigns/campaignsSlice';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useSlideOverContext } from '@/hooks/useSlideOverContext';
import { FrontTable, TablePager } from '@/lib';
import { Audience } from '@/models/audiences';
import { RootState } from '@/reducers';
import { useGetAudiencesCountQuery, useLazyGetAudiencesQuery } from '@/services/endpoints/audiences';
import { Row } from '@tanstack/react-table';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getAudiencesTableColumns } from './helpers';

export const AudiencesTable: FC = () => {
  const { t } = useTranslation();
  const workspace = useWorkspace();
  const { openSlideOver } = useSlideOverContext();

  const [getAudiences, { data, isFetching: isAudiencesFetching, isLoading: isAudiencesLoading }] = useLazyGetAudiencesQuery();
  const [audiences, setAudiences] = useState<Audience[]>([]);

  const searchText = useSelector((state: RootState) => state.campaigns.create.audiences.searchText);
  const validSearchText = searchText && searchText.length > 2 ? searchText : undefined;

  const { data: totalCount, isFetching: isTotalCountFetching } = useGetAudiencesCountQuery({ workspaceId: workspace.id, searchText: validSearchText });

  const queryCallback = useCallback(
    (pageNo: number, pageSize: number) =>
      getAudiences({
        workspaceId: workspace.id,
        offset: pageNo * pageSize,
        limit: pageSize,
        searchText: validSearchText,
      }),
    [validSearchText]
  );

  const handleRowClick = (row: Row<Audience>) => {
    openSlideOver({
      renderContent: AudienceDetailSlideOver,
      renderContentProps: { audienceId: row.original.id },
      showButtonInsideSlideOver: true,
    });
  };

  const columns = useMemo(() => getAudiencesTableColumns(t), [t]);

  return (
    <TablePager
      stateSelector={(state) => state.campaigns.create.audiences.pagination}
      stateUpdater={setCreateCampaignAudiencesPagination}
      query={queryCallback}
      queryResponse={data ? data.items : undefined}
      setItems={setAudiences}
      querying={isTotalCountFetching || isAudiencesFetching}
      totalCount={totalCount}
      wrapperClassNames='mb-0'
    >
      {(pager) => (
        <Loadable isLoading={isAudiencesLoading}>
          <FrontTable tableData={audiences} columns={columns} footer={pager} onRowClick={handleRowClick} />
        </Loadable>
      )}
    </TablePager>
  );
};
